"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Mic,
  Sparkles,
  Crown,
  Stethoscope,
  Brain,
  Heart,
  Eye,
  Globe2,
  Shield,
  Users,
  Award,
  Building2,
  Briefcase,
  BookOpen,
  ArrowRight,
  Phone,
  CheckCircle2,
  Layers,
  Compass,
  Flame,
  Trophy,
  Target,
  Activity,
  Zap,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

const SIGNATURE_KEYNOTES = [
  {
    icon: Stethoscope,
    title: "How Excellence Actually Runs",
    tagline: "Excellence is a system, not a value.",
    body: "The four components of an operating system for excellence — protocols, checklists, reviews, outcome accountability — drawn from sovereign-scale operations where the margin for error was zero and the consequences of failure were national.",
    accent: "red",
    href: "/speaking/how-excellence-actually-runs/",
    duration: "45–60 min",
  },
  {
    icon: Brain,
    title: "Leadership as Diagnosis",
    tagline: "What medicine teaches about running organizations.",
    body: "Six clinical principles — diagnose before treating, triage under pressure, run the protocol, own the outcome, do no harm, regulate the room — applied to the leaders who keep organizations functioning when the stakes are highest.",
    accent: "purple",
    href: "/speaking/leadership-as-diagnosis/",
    duration: "40–55 min",
  },
  {
    icon: Heart,
    title: "The 70% Project",
    tagline: "The operating case for the talent the world overlooks.",
    body: "Five operating advantages that majority-female teams consistently demonstrate — communication, risk discipline, collaboration, retention, ego management. The argument is from operations, not morality. The evidence is a decade of sovereign-scale results.",
    accent: "emerald",
    href: "/speaking/the-70-percent-project/",
    duration: "45–60 min",
  },
  {
    icon: Eye,
    title: "Build in the Dark",
    tagline: "The operator's discipline in an attention economy.",
    body: "Three disciplines that produce more durable results than any public relations strategy — outcome accountability over effort signaling, duration over disruption, systems over charisma. The things that endure are built without an audience.",
    accent: "denim",
    href: "/speaking/build-in-the-dark/",
    duration: "35–50 min",
  },
  {
    icon: Globe2,
    title: "The Triple Threat",
    tagline: "Medicine, technology, and politics as one operating method.",
    body: "Clinical precision applied to technological systems under political-grade stakes. The intersection no other executive occupies — and the source of the competitive advantage that has produced fifteen out of eighteen campaign victories.",
    accent: "amber-gold",
    href: "/speaking/the-triple-threat/",
    duration: "50–65 min",
  },
  {
    icon: Users,
    title: "Women Breaking Barriers",
    tagline: "Lessons from operating at sovereign scale as a woman.",
    body: "The operational reality of leading in rooms where women are rarely present. Seven practical strategies, three operating principles, and a decade of evidence that the most effective response to barriers is operational proof.",
    accent: "coral",
    href: "/speaking/women-breaking-barriers/",
    duration: "40–55 min",
  },
];

const ADDITIONAL_TOPICS = [
  {
    icon: Shield,
    title: "AI Governance Lessons",
    body: "Governing AI platforms across multiple jurisdictions — constraint definition, outcome verification, accountability architecture. The distance between AI ethics theory and AI operations at sovereign scale.",
    accent: "cyan",
    href: "/speaking/ai-governance-lessons/",
  },
  {
    icon: Flame,
    title: "Leading Through Crisis",
    body: "The three-pillar crisis response architecture — perception containment, threat intelligence, communications security. Crisis is inevitable. Transformation is optional. The architecture that decides which is engineered before the crisis arrives.",
    accent: "red",
    href: "/speaking/leading-through-crisis/",
  },
  {
    icon: Heart,
    title: "Future of Women's Health",
    body: "A physician's view on how AI is transforming women's healthcare — and what the medical profession can teach the technology sector about patient-centered design, clinical realities, and governance discipline.",
    accent: "lime",
    href: "/speaking/future-of-womens-health/",
  },
  {
    icon: Compass,
    title: "Building Diverse Teams",
    body: "The operational mechanics of assembling, leading, and retaining cross-functional, multicultural teams — five predictive signals, communication architecture, retention system, ego management discipline.",
    accent: "purple",
    href: "/speaking/building-diverse-teams/",
  },
];

const HEADLINE_STATS = [
  { metric: "6", suffix: "", label: "Signature Keynotes", sub: "Plus four additional topics", accent: "red" },
  { metric: "15/18", suffix: "", label: "Campaign Victories", sub: "Verified across sovereign engagements", accent: "purple" },
  { metric: "30", suffix: "+", label: "Countries Engaged", sub: "Three continents of operations", accent: "cyan" },
  { metric: "0", suffix: "", label: "Security Incidents", sub: "15+ years sovereign-grade", accent: "emerald" },
];

const WHY_BOOK = [
  {
    icon: Stethoscope,
    title: "The Medical Method",
    body: "No other speaker at this level brings a physician's discipline to the stage. The clinical method is not metaphor — it is the operating system that has produced sovereign-scale outcomes.",
    accent: "red",
  },
  {
    icon: Trophy,
    title: "Sovereign-Scale Credibility",
    body: "When the audience includes leaders operating at the highest levels, the speaker must have operated at that level. Verified outcomes across eighteen countries and over three hundred elite clients.",
    accent: "amber-gold",
  },
  {
    icon: Target,
    title: "Operational Education, Not Motivation",
    body: "Every takeaway is drawn from verified operational experience. Protocols, checklists, decision frameworks — the audience leaves with systems, not inspiration. The distinction is critical.",
    accent: "emerald",
  },
  {
    icon: Layers,
    title: "Cross-Domain Authority",
    body: "Medicine, technology, and politics — integrated as one operating method. The intersection that produces outcomes no single-domain specialist can replicate.",
    accent: "purple",
  },
  {
    icon: Compass,
    title: "Tailored Delivery",
    body: "Every keynote is adapted to the audience, the event, and the stakes. The diagnostic phase precedes the keynote — understanding the room before prescribing the message.",
    accent: "cyan",
  },
  {
    icon: Award,
    title: "Track Record of Impact",
    body: "Nobel Peace Prize. World Economic Forum. Engagements alongside governments and global leaders. The caliber of the stage reflects the caliber of the work.",
    accent: "coral",
  },
];

const FORMATS = [
  { label: "Keynote Address", duration: "35–65 min", body: "Structured presentation from core argument through evidence to actionable takeaways. The primary delivery mode." },
  { label: "Fireside Chat", duration: "30–45 min", body: "Conversational depth guided by a moderator. Ideal for executive gatherings and board retreats where dialogue produces more than monologue." },
  { label: "Panel Participation", duration: "20–30 min", body: "Cross-domain perspective contributed to multi-speaker discussions on operational excellence, leadership, governance, or technology." },
  { label: "Workshop / Masterclass", duration: "2–4 hrs", body: "Deep-dive operational training applying the clinical method to specific organizational challenges. Tangible output. Implementation-ready frameworks." },
  { label: "Executive Briefing", duration: "60–90 min", body: "Private, invitation-only sessions for leadership teams seeking to implement the operating method within their organizations. The most confidential format." },
  { label: "Virtual / Remote", duration: "All formats", body: "All formats available for virtual events. The same operational authority and engagement quality, optimized for distributed audiences." },
];

const PROCESS = [
  { num: "01", title: "Inquiry", body: "The engagement begins with an inquiry through the contact channels — specifying the event, audience, topic preference, date, and format. The inquiry is the first diagnostic step." },
  { num: "02", title: "Consultation", body: "A preliminary conversation to understand the event objectives, audience profile, and desired outcomes. The diagnostic phase — understanding the situation before prescribing the keynote." },
  { num: "03", title: "Customization", body: "The keynote is tailored to the audience and the event. Operating method universal. Application precise. Research into the audience's domain, their challenges, their specific takeaways." },
  { num: "04", title: "Delivery", body: "The keynote is delivered with the same operational discipline that governs every engagement. Preparation, precision, accountability for the outcome. Not rehearsed performance. Operational briefing." },
  { num: "05", title: "Follow-Up", body: "Post-event resources, frameworks, and follow-up sessions available for organizations seeking to implement the operating method within their teams. Lasting change, not temporary elevation." },
];

const AUDIENCES = [
  { icon: Crown, title: "C-Suite & Boards", body: "Decision-makers who recognize their organization has an execution problem and need an operating system, not another motivational speech.", accent: "red" },
  { icon: Building2, title: "Government & Sovereign Leaders", body: "Decision-makers operating at the level where the stakes are national. The sovereign-scale evidence base eliminates the discount executive audiences apply to consulting frameworks.", accent: "purple" },
  { icon: Shield, title: "Defense & Security Forums", body: "Organizations focused on national security, intelligence operations, and defense coordination. The cross-domain perspective integrates clinical precision with technological capability.", accent: "denim" },
  { icon: Briefcase, title: "Fortune-Scale Corporations", body: "Brand protection, crisis response, competitive positioning, operational transformation. The same sovereign-grade infrastructure deployed for governments — not a diluted version.", accent: "amber-gold" },
  { icon: Users, title: "Women's Leadership Organizations", body: "Forums seeking a role model who has built, not just advocated. The 70% Project is the evidence — measured outcomes across eighteen countries and over three hundred elite clients.", accent: "coral" },
  { icon: BookOpen, title: "Universities & Executive Education", body: "Academic institutions seeking to expose students and executives to the operating method that produces verified outcomes. The clinical method is teachable, repeatable, scalable.", accent: "cyan" },
];

const GAINS = [
  "Operating systems, not inspiration — protocols, checklists, decision frameworks designed for implementation the next day",
  "The medical method — a teachable, repeatable, scalable approach to leadership that no other speaker at this level provides",
  "The 70% Operating Model — evidence-based insights into building high-performance diverse teams drawn from a decade of operations",
  "Sovereign-scale perspective — lessons from operations where the stakes are national and the margin for error is zero",
  "Immediate applicability — every insight is drawn from verified operational experience and designed to be implemented tomorrow",
  "Cross-domain integration — the perspective that bridges medicine, technology, and politics at the most demanding levels of consequence",
];

export default function SpeakingHubPage() {
  return (
    <>
      <PageHero
        icon={Mic}
        overline="Keynote Speaking & Engagements"
        overlineClassName="text-red"
        title={
          <>
            Operational Authority, <span className="text-gradient-premium">Delivered from the Stage.</span>
          </>
        }
        subtitle="Dr. Jyoti Kush delivers keynotes that are not theoretical frameworks. They are the operational lessons of a physician who spent a decade building and running a sovereign-grade digital conglomerate across eighteen countries, with teams that are over seventy percent women. Six signature topics. Each grounded in sovereign-scale operations. Each delivered with the clinical precision and commanding authority that define the operating method."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Speaking" }]}
        badge="Six signature keynotes · Sovereign-scale evidence"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-5`}>
              <Sparkles className="size-3.5 text-purple" />
              <span className="text-overline text-purple">Executive Summary</span>
            </div>
            <h2 className="text-display-md mb-5">
              Operating systems, not inspiration.
            </h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              The speaking is available for conferences, corporate events, universities, leadership forums, and private engagements. Every keynote is rooted in the same discipline that has produced a one-hundred-percent client satisfaction rate, a ninety-nine-point-nine-nine-nine-nine percent operational uptime, fifteen out of eighteen political campaigns won, and zero security incidents across fifteen-plus years of sovereign-grade operations.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              The six signature topics span operational excellence, leadership methodology, women&apos;s empowerment, entrepreneurship discipline, governance, and the intersection of medicine, technology, and politics. Each topic is tailored to the audience and the stakes — because the same operating method that governs sovereign-level campaigns also governs the preparation and delivery of every keynote.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              Organizations that book Dr. Jyoti Kush are not hiring a motivational speaker. They are engaging an operator whose entire career has been building and running systems where failure is not an option — and whose insights are drawn from that operational reality.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {HEADLINE_STATS.map((s, i) => {
              const a = ACCENT[s.accent];
              return (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
                >
                  <div className={`text-display-sm font-bold ${a.text} leading-none mb-2`}>
                    {s.metric}{s.suffix}
                  </div>
                  <div className="text-caption font-bold uppercase tracking-wider mb-1">{s.label}</div>
                  <div className="text-caption text-muted-foreground">{s.sub}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-4`}>
            <Mic className="size-3.5 text-red" />
            <span className="text-overline text-red">The Six Signature Keynotes</span>
          </div>
          <h2 className="text-display-md mb-4">Six topics drawn from sovereign-scale operations.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Each signature keynote is grounded in a decade of verified operational experience. Each can be delivered independently or as part of a comprehensive speaking series. Each leaves the audience with systems, not inspiration.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {SIGNATURE_KEYNOTES.map((k, i) => {
            const a = ACCENT[k.accent];
            return (
              <motion.div
                key={k.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                <div className="relative">
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center shadow-lg shrink-0`}>
                      <k.icon className="size-6 text-white" />
                    </div>
                    <span className={`text-caption font-semibold ${a.text}`}>{k.duration}</span>
                  </div>
                  <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{k.title}</h3>
                  <p className="text-caption font-semibold text-muted-foreground mb-3 italic">{k.tagline}</p>
                  <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">{k.body}</p>
                  <Link
                    href={k.href}
                    className={`inline-flex items-center gap-1.5 text-caption font-semibold ${a.text} hover:gap-2.5 transition-all`}
                  >
                    Explore the keynote <ArrowRight className="size-3" />
                  </Link>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Layers className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">Additional Topics</span>
          </div>
          <h2 className="text-display-md mb-4">Beyond the six signatures.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Four additional keynotes tailored to specific domains and audiences. Each draws from the same operational experience that grounds the six signature topics — verified outcomes, sovereign-scale operations, and the clinical method applied to real-world challenges.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {ADDITIONAL_TOPICS.map((t, i) => {
            const a = ACCENT[t.accent];
            return (
              <motion.div
                key={t.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="group"
              >
                <Link href={t.href} className="block h-full">
                  <div className={`relative glass-card rounded-2xl p-5 h-full bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all overflow-hidden`}>
                    <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                    <div className="relative">
                      <div className={`size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center mb-3 shadow-md shrink-0`}>
                        <t.icon className="size-5 text-white" />
                      </div>
                      <h3 className={`text-heading-sm mb-2 ${a.text}`}>{t.title}</h3>
                      <p className="text-caption text-muted-foreground leading-relaxed mb-3">{t.body}</p>
                      <div className={`inline-flex items-center gap-1.5 text-caption font-semibold ${a.text} group-hover:gap-2.5 transition-all`}>
                        Explore <ArrowRight className="size-3" />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Briefcase className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">Speaking Format Options</span>
          </div>
          <h2 className="text-display-md mb-4">Flexibility for every event configuration.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Six delivery formats. From the structured keynote to the most confidential executive briefing. Each format is calibrated to the audience, the event, and the outcomes sought.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {FORMATS.map((f, i) => {
            const accents = ["red", "purple", "cyan", "emerald", "amber-gold", "denim"];
            const a = ACCENT[accents[i % accents.length]];
            return (
              <motion.div
                key={f.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className={`text-heading-sm ${a.text}`}>{f.label}</h3>
                  <span className={`text-caption font-bold ${a.text}`}>{f.duration}</span>
                </div>
                <p className="text-caption text-muted-foreground leading-relaxed">{f.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-5`}>
              <Target className="size-3.5 text-amber-gold" />
              <span className="text-overline text-amber-gold">Who Benefits Most</span>
            </div>
            <h2 className="text-display-md mb-4">The caliber of audience served.</h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              The organizations that engage the speaking services are those operating at the highest levels of consequence — where the margin for error is zero and the audience demands substance. From sovereign-level decision-makers to executive women seeking a role model who has built, not just advocated.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              The same operating method that governs sovereign-level campaigns governs the preparation of every keynote. The diagnostic phase precedes the engagement. The customization is structural, not cosmetic.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {AUDIENCES.map((a, i) => {
              const ac = ACCENT[a.accent];
              return (
                <motion.div
                  key={a.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${ac.bg} to-transparent border ${ac.border} hover:scale-[1.005] transition-all`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                      <a.icon className="size-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className={`text-heading-sm mb-1.5 ${ac.text}`}>{a.title}</h3>
                      <p className="text-caption text-muted-foreground leading-relaxed">{a.body}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Trophy className="size-3.5 text-denim" />
            <span className="text-overline text-denim">The Decision-Maker&apos;s Case</span>
          </div>
          <h2 className="text-display-md mb-4">Why book Dr. Jyoti Kush.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Six structural advantages — verified through fifteen-plus years of sovereign-scale operations, fifteen campaign victories, and zero security incidents.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {WHY_BOOK.map((w, i) => {
            const a = ACCENT[w.accent];
            return (
              <motion.div
                key={w.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <w.icon className="size-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-sm mb-1.5 ${a.text}`}>{w.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{w.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.lime.pill} mb-4`}>
            <CheckCircle2 className="size-3.5 text-lime" />
            <span className="text-overline text-lime">What Attendees Gain</span>
          </div>
          <h2 className="text-display-md mb-4">The value of attending.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The distinction between keynote speaking and keynote education. One produces temporary elevation. The other produces permanent capability.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {GAINS.map((g, i) => {
            const accents = ["red", "purple", "emerald", "amber-gold", "cyan", "denim"];
            const a = ACCENT[accents[i % accents.length]];
            return (
              <motion.div
                key={g}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`glass-card rounded-xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-9 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <CheckCircle2 className={`size-4 text-white`} />
                  </div>
                  <p className="text-caption text-muted-foreground leading-relaxed flex-1">{g}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.coral.pill} mb-4`}>
            <Activity className="size-3.5 text-coral" />
            <span className="text-overline text-coral">The Process</span>
          </div>
          <h2 className="text-display-md mb-4">From inquiry to delivery.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The engagement process mirrors the diagnostic method that underpins the keynotes themselves. Understanding before prescribing. Every step designed to produce the most relevant keynote for the specific room.
          </p>
        </div>
        <div className="space-y-3 max-w-4xl mx-auto">
          {PROCESS.map((p, i) => {
            const accents = ["red", "purple", "cyan", "emerald", "amber-gold"];
            const a = ACCENT[accents[i % accents.length]];
            return (
              <motion.div
                key={p.num}
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`group glass-card rounded-2xl p-5 lg:p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="grid lg:grid-cols-[auto_1fr_auto] gap-5 items-center">
                  <div className={`size-14 rounded-2xl bg-gradient-sovereign flex items-center justify-center shadow-xl ring-2 ${a.ring} group-hover:scale-110 group-hover:rotate-3 transition-all shrink-0`}>
                    <span className="text-heading-md font-bold text-white">{p.num}</span>
                  </div>
                  <div>
                    <h3 className={`text-heading-md mb-1 ${a.text}`}>{p.title}</h3>
                    <p className="text-body-sm text-muted-foreground leading-relaxed">{p.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-5`}>
              <Zap className="size-3.5 text-red" />
              <span className="text-overline text-red">Speaking Philosophy</span>
            </div>
            <h2 className="text-display-md mb-4">Why this speaking is different.</h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              Most keynote speakers theorize. The speaking operationalizes. Every insight must be drawn from verified operational experience. Every framework must have been tested under real-world conditions. Every principle must have produced measurable outcomes.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              The medical background adds a dimension that no other speaker at this level brings. The discipline of medicine — diagnosis before treatment, triage under pressure, outcome accountability — is not a metaphor in these keynotes. It is the actual operating system being taught.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              Every keynote is designed to be immediately applicable. The audience leaves not with inspiration but with operating systems — protocols, checklists, decision frameworks, and the clinical discipline to implement them. This is not motivational speaking. This is operational education delivered from the stage.
            </p>
          </div>
          <div className="space-y-3">
            {[
              { title: "Every insight is operational.", body: "Drawn from verified sovereign-scale experience, not from theory or aspiration.", accent: "red" },
              { title: "Every framework is tested.", body: "Produced under real-world conditions where the margin for error was zero and the stakes were national.", accent: "purple" },
              { title: "Every keynote is bespoke.", body: "Calibrated to the audience&apos;s domain, stakes, and capacity for implementation. Not genericized.", accent: "cyan" },
              { title: "Every takeaway is implementable.", body: "Protocols, checklists, decision frameworks designed to be deployed the next business day.", accent: "emerald" },
              { title: "Every engagement is sovereign-grade.", body: "National security level confidentiality. The same operational discipline that governs the organization.", accent: "amber-gold" },
            ].map((p, i) => {
              const a = ACCENT[p.accent];
              return (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, x: 16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`size-9 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                      <CheckCircle2 className="size-4 text-white" />
                    </div>
                    <div>
                      <h3 className={`text-heading-sm mb-1 ${a.text}`}>{p.title}</h3>
                      <p className="text-caption text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: p.body }} />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.amber_gold?.pill || ACCENT["amber-gold"].pill} mb-4`}>
            <Award className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">Past Stages</span>
          </div>
          <h2 className="text-display-md mb-4">Where the speaking has resonated.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The caliber of the stage reflects the caliber of the work. The speaking is delivered where the stakes are real and the audience demands substance.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { label: "The Nobel Peace Prize", body: "Among the most prestigious gatherings in the world." },
            { label: "The World Economic Forum", body: "Where global leaders convene to address the world&apos;s most pressing challenges." },
            { label: "International Leadership Forums", body: "Across multiple continents and sovereign audiences." },
            { label: "Government & Sovereign Engagements", body: "Decision-makers at the highest levels of consequence." },
            { label: "Fortune-Scale Corporate Events", body: "Organizations seeking operational authority, not theoretical frameworks." },
            { label: "University & Academic Forums", body: "The next generation of leaders equipped with operating systems." },
            { label: "Defense & Security Conferences", body: "The intersection of technology, governance, and national security." },
            { label: "Women&apos;s Leadership Summits", body: "The operating case for women in leadership, presented with evidence." },
          ].map((s, i) => {
            const accents = ["red", "purple", "cyan", "emerald", "amber-gold", "denim", "coral", "lime"];
            const a = ACCENT[accents[i % accents.length]];
            return (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`glass-card rounded-xl p-4 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
              >
                <h3 className={`text-heading-sm mb-1.5 ${a.text}`} dangerouslySetInnerHTML={{ __html: s.label }} />
                <p className="text-caption text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: s.body }} />
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-red/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-amber-gold/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Sparkles className="size-10 text-red mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Begin an engagement.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Every keynote customized to the audience, the event, and the stakes. National security level confidentiality from first contact. The diagnostic phase precedes the engagement — understanding the situation before prescribing the keynote.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Phone className="size-4" />
                Submit a Booking Inquiry
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/about/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Crown className="size-4" />
                About Dr. Jyoti Kush
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "About Dr. Jyoti Kush", description: "The full story of the physician who became an operator at sovereign scale.", href: "/about/", icon: Crown, accent: "red" },
          { title: "The 70% Project", description: "The operating case for the talent the world overlooks.", href: "/the-70-percent-project/", icon: Heart, accent: "emerald" },
          { title: "The Operator&apos;s Notebook", description: "Biweekly notes on how excellent things actually run.", href: "/the-operators-notebook/", icon: BookOpen, accent: "cyan" },
          { title: "Services", description: "Engagement modalities calibrated to the stakes and the audience.", href: "/services/", icon: Briefcase, accent: "amber-gold" },
        ]}
      />
    </>
  );
}
